/**
 * @file    useWidgetOptions.ts
 * @scope   SHARED
 * @summary Change handlers for the drawer "Options"/"Actions" checkboxes and their child inputs.
 * @consumes Global store writer (`setState`), `optionsPropertyWidget` constants
 * @produces `handleActions`, `handleOptions`, `handleDecimalChange`, `getSetter`, `getSetterValues`
 * @sideEffects Publishes the edited widget back to the global store on every change.
 * @notes The goal-line / y-axis setters are owned by DiyDrawer (they feed dedicated effects), so
 *        they are injected rather than created here.
 */
import React from 'react';
import { optionsPropertyWidget } from 'constants/customOptionConstants';

/** Options whose child component writes into a dedicated piece of drawer state. */
const CHILD_COMPONENT_GOAL_LINE = 'GoalLine';
const CHILD_COMPONENT_Y_AXIS = 'YAxisValueComponent';

export interface UseWidgetOptionsParams {
  customWidget: any;
  setState: (partial: any) => void;
  setDecimalPlaces: React.Dispatch<React.SetStateAction<any>>;
  setGoalValues: React.Dispatch<React.SetStateAction<any>>;
  setYAxisValues: React.Dispatch<React.SetStateAction<any>>;
}

export interface WidgetOptions {
  /** Toggles an entry of `options.actions`. */
  handleActions: (event: { target: { name: string; checked: boolean } }) => void;
  /** Toggles a widget option (root level, with data-grid specific defaults). */
  handleOptions: (event: { target: { name: string; checked: boolean } }) => void;
  /** Applies a new decimal-places selection. */
  handleDecimalChange: (event: { label: string; value: number; name: string }, target: any) => void;
  /** Resolves the state setter backing an option's child component. */
  getSetter: (key: string) => React.Dispatch<React.SetStateAction<any>>;
  /** Resolves the current value backing an option's child component. */
  getSetterValues: (key: string, options: any) => any;
}

export function useWidgetOptions({
  customWidget,
  setState,
  setDecimalPlaces,
  setGoalValues,
  setYAxisValues,
}: UseWidgetOptionsParams): WidgetOptions {
  const setters: { [key: string]: React.Dispatch<React.SetStateAction<any>> } = {
    [CHILD_COMPONENT_GOAL_LINE]: setGoalValues,
    [CHILD_COMPONENT_Y_AXIS]: setYAxisValues,
  };

  const getSetter = (key: string) => setters[key];

  const getSetterValues = (key: string, options: any) => {
    switch (key) {
      case CHILD_COMPONENT_GOAL_LINE:
        return options.goal;
      case CHILD_COMPONENT_Y_AXIS:
        return options.optYAxisValues;
      default:
        return null;
    }
  };

  const handleActions = (event: { target: { name: string; checked: boolean } }) => {
    const newActions = [...(customWidget.options.actions || [])];
    if (newActions.includes(event.target.name)) {
      !event.target.checked && newActions.splice(newActions.indexOf(event.target.name), 1);
    } else {
      event.target.checked && newActions.push(event.target.name);
    }
    const newOptions = { ...customWidget.options, actions: newActions };
    setState({ customWidget: { ...customWidget, options: newOptions } });
  };

  const handleDecimalChange = (
    event: { label: string; value: number; name: string },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _target: any
  ) => {
    const newOptions: any = { ...customWidget.options };
    newOptions[optionsPropertyWidget.decimalPlaces] = event.value;
    setState({ customWidget: { ...customWidget, options: newOptions } });
    // set state for decimal
    setDecimalPlaces(event.value);
  };

  const handleOptions = (event: { target: { name: string; checked: boolean } }) => {
    const newOptions: any = { ...customWidget.options };

    if (customWidget.alias === 'DynamicTable') {
      applyBarLineDefault(newOptions);
      newOptions[event.target.name as keyof typeof newOptions] = event.target.checked;
      // WHY: the data-grid branch deliberately re-spreads `customWidget.options` so any key the
      // clone did not carry over survives the write. Preserved verbatim from the original code.
      setState({
        customWidget: { ...customWidget, options: { ...customWidget.options, ...newOptions } },
      });
      return;
    }

    // WHY: `decimalPlaces` is a numeric option rendered as a checkbox, so enabling it means "2".
    newOptions[event.target.name as keyof typeof newOptions] =
      event.target.name === 'decimalPlaces' ? 2 : event.target.checked;
    applyBarLineDefault(newOptions);
    setState({ customWidget: { ...customWidget, options: newOptions } });
  };

  return { handleActions, handleOptions, handleDecimalChange, getSetter, getSetterValues };
}

/** Mirrors the `showLineChart` toggle into the chart's default rendering mode. */
function applyBarLineDefault(options: any): void {
  if ('showLineChart' in options) {
    options.defaults = {
      ...options.defaults,
      SWITCH_BAR_LINE: options.showLineChart ? 'line' : 'bars',
    };
  }
}

export default useWidgetOptions;
